import React, { Dispatch, JSX, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Element as ScrollTo } from 'react-scroll';
import { Button, Col, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import Keys from '../../../Translations/generated/Checkout.de.json.keys';
import fallback from '../../../Translations/generated/Checkout.de.json';
import { BookingService } from '@ibe/services';
import { ApiBaseData, ApiTraveler, ApiTravelerType } from '@ibe/api';
import { CustomInput, FormBuilder, FormRef, useConfig, useTranslation } from '@ibe/components';
import ContentContainer from '../../../Components/ContentContainer';
import { FormItemsProps } from '../../../Config/useTravellerFormConfigProductIBE';
import { getFormConfig, getSalutations } from './externals';
import { scrollElementErrorTravelerBase } from './Traveler';
import getTravelerMockData from '../../../Config/mockData';
import { InputSearchDropdownOption } from '@ibe/components/dist/ISO/Form/types/types';
import { isEqual } from 'lodash-es';
import debounce from 'lodash-es/debounce';
import useGooglePlaces from './GooglePlacesAutocomplete';

interface Props {
  travelers: ApiTraveler[];
  setTravelers: Dispatch<React.SetStateAction<ApiTraveler[]>>;
  bs: BookingService;
  sameAddressForms: ApiTraveler[];
  setSameAddressForms: Dispatch<React.SetStateAction<ApiTraveler[]>>;
  formRefs: RefObject<FormRef<Record<string, unknown>>[]>;
  externalData: {
    COUNTRIES: ApiBaseData[];
    SALUTATIONS: ApiBaseData[];
    NATIONALITIES: ApiBaseData[];
  };
  initialValues?: Array<{ [key: string]: string }>;
}

const TravelerForms = (props: Props): JSX.Element => {
  const {
    travelers,
    setTravelers,
    bs,
    sameAddressForms,
    setSameAddressForms,
    formRefs,
    externalData,
    initialValues
  } = props;
  const { t } = useTranslation('Checkout', fallback);
  const config = useConfig();

  const [initialMockValues, setInitialMockValues] = useState<Record<string, unknown>[] | undefined>(
    undefined
  );

  useEffect(() => {
    if (initialMockValues) {
      formRefs.current.forEach((ref: FormRef<Record<string, unknown>>, idx: number) => {
        ref?.resetForm(initialMockValues[idx] || initialMockValues[0]);
      });
    }
  }, [initialMockValues]);

  const handleCheckboxChange = (formItem: ApiTraveler): void => {
    setSameAddressForms(value =>
      value.find(form => form.id === formItem.id)
        ? value.filter(form => form.id !== formItem.id)
        : [...value, formItem]
    );
  };

  const { loadGooglePlacesScript, searchGooglePlaces, getAddressFromGooglePlace } = useGooglePlaces(
    config
  );
  const prevStreetValue = useRef<Record<string, string>>({});
  const [streetSuggestions, setStreetSuggestions] = useState<
    Record<string, InputSearchDropdownOption[]>
  >({});
  const debouncedSearchRef = useRef(
    debounce(async (query: string, id: string) => {
      const suggestions = await searchGooglePlaces(query);
      setStreetSuggestions(prev => ({ ...prev, [id]: suggestions }));
    }, 300)
  );

  useEffect(() => {
    loadGooglePlacesScript();
  }, []);

  const updateGooglePlaceAutocomplete = useCallback(
    async (values: Record<string, string>, id: string) => {
      const changedTraveler = travelers.find(traveler => traveler.id === id);
      if (!changedTraveler) return;

      const changedStreetValue = prevStreetValue.current[id];
      prevStreetValue.current[id] = values.street;

      // last value and field value are the same -> return
      if (changedStreetValue === values.street) {
        return;
      }

      if (!values.street || values.street.length < 2) {
        setStreetSuggestions({});
        return;
      }

      debouncedSearchRef.current(values.street, id);
    },
    []
  );

  const updateTravelers = useCallback(
    (values: Record<string, string>, id: string) => {
      const formValues = values as FormItemsProps;
      const { country, zipCode, street, city, email, phone, ...otherFormValues } = formValues;

      setTravelers(prevTravelers => {
        const changedTraveler = prevTravelers.find(traveler => traveler.id === id);
        if (!changedTraveler) return prevTravelers;

        const newTravelers = prevTravelers.map(traveler => {
          if (traveler.id === id) {
            return {
              ...traveler,
              ...otherFormValues,
              address: {
                ...{ countryCode: '', postalCode: '', street: '', city: '' },
                ...traveler?.address,
                countryCode: country,
                street,
                postalCode: zipCode,
                city
              },
              communicationDetails: { ...traveler.communicationDetails, phone, email }
            };
          }
          return traveler;
        });

        return isEqual(prevTravelers, newTravelers) ? prevTravelers : newTravelers;
      });
    },
    [travelers]
  );

  const onFormFieldsChange = (values: Record<string, string>, id: string) => {
    updateGooglePlaceAutocomplete(values, id);
    updateTravelers(values, id);
  };

  return (
    <ContentContainer className={`travellerData ibeFlightClassName`}>
      <h1 className="traveler-header font-weight-bold mb-4">{t(Keys.fillInParticipantsData)}</h1>
      <div className="info-name-badge mb-4">
        <FontAwesomeIcon icon={faInfoCircle} />
        <div>{t(Keys.customerNameInfo)}</div>
      </div>
      {config?.traveler?.mockAvailable && (
        <div className="d-flex justify-content-end">
          <Button
            color="secondary"
            className="mb-4 mr-3"
            aria-hidden="true"
            aria-disabled="true"
            tabIndex={-1}
            onClick={(): void => {
              setInitialMockValues(
                [...Array(travelers.length).keys()].map(() => getTravelerMockData())
              );
            }}
          >
            Fill with mock data
          </Button>
        </div>
      )}
      {travelers.map((item, idx) => {
        const personIndex =
          (travelers
            .filter(localItem => localItem.type === item.type)
            .findIndex(localItem => localItem.id === item.id) || 0) + 1;
        const isFirstAdult = personIndex === 1 && item.type === ApiTravelerType.ADULT;
        const useFirstAddress = !!sameAddressForms.find(form => form.id === item.id);

        const fieldSetTitle = (
          <>
            {`${personIndex}. ${t((Keys as Record<string, string>)[item.type])}`}&nbsp;
            {isFirstAdult && (
              <span className="form-module__headline--suffix">{'(' + t(Keys.customer) + ')'}</span>
            )}
          </>
        );

        return (
          <ScrollTo key={item.id} name={scrollElementErrorTravelerBase + idx}>
            <div className="mb-4" key={item.id}>
              <div className="form-module__headline">
                <Row>
                  <Col lg={isFirstAdult ? 5 : 12}>
                    <div>{fieldSetTitle}</div>
                  </Col>
                  {isFirstAdult && (
                    <Col lg={7}>
                      <p>{t(Keys.customerInfo)}</p>
                    </Col>
                  )}
                </Row>
              </div>
              <FormBuilder
                key={item.id}
                formConfig={getFormConfig(
                  useFirstAddress,
                  idx === 0,
                  initialValues ? initialValues[idx] : undefined,
                  config,
                  idx
                )}
                exposeSeparateFieldValue={async (fieldName, fullObject) => {
                  // gets called on dropdown-select
                  const address = await getAddressFromGooglePlace(
                    (fullObject as unknown) as google.maps.places.Place
                  );

                  const street = address.street ?? undefined;
                  if (street) {
                    formRefs.current[idx]?.setValue(
                      'street',
                      [street, address.houseNumber == '' ? null : address.houseNumber].join(' ')
                    );
                  }

                  formRefs.current[idx]?.setValue('zipCode', address.zip ?? '');
                  formRefs.current[idx]?.setValue('city', address.city ?? '');
                  formRefs.current[idx]?.setValue('country', address.country ?? '');
                }}
                onFormFieldsChange={(values: Record<string, string>) =>
                  onFormFieldsChange(values, item.id)
                }
                // eslint-disable-next-line
                // @ts-ignore
                ref={(ref: FormRef<Record<string, unknown>> | null): void => {
                  if (ref) {
                    formRefs.current[idx] = ref;
                  }
                }}
                id={idx.toString()}
                withFormTag={false}
                hideSubmitButton
                scrollToError
                initialValues={{ id: item.id }}
                externalData={{
                  countries: externalData.COUNTRIES,
                  salutations: getSalutations(externalData.SALUTATIONS, item.type),
                  nationalities: externalData.NATIONALITIES,
                  hideFields: !!sameAddressForms.find(form => form.id === item.id),
                  isFirstAdult: isFirstAdult,
                  yupContext: {
                    adultMinAge: config.traveler.maxChildAge + 1,
                    childMinAge: 0,
                    startDate: bs.booking?.travelStartDate
                  },
                  googlePlacesResults: streetSuggestions[item.id] ?? []
                }}
                mode="all"
                reValidateMode="onBlur"
              >
                <div />
                {!isFirstAdult && (
                  <div>
                    <CustomInput
                      type="checkbox"
                      onChange={(): void => handleCheckboxChange(item)}
                      checked={useFirstAddress}
                      label={t(Keys.sameAddressAsFirstAdult)}
                      id={`checkoutSameAddress-${item.id}`}
                      className="font-weight-bold pt-3"
                    />
                  </div>
                )}
              </FormBuilder>
              <Row>
                <Col md={10} className="d-flex justify-content-end">
                  <div className="form-module__lower-label">{t(Keys.requiredFields)}</div>
                </Col>
              </Row>
            </div>
          </ScrollTo>
        );
      })}
    </ContentContainer>
  );
};

export default TravelerForms;
